import React, { useState, useEffect } from 'react';
import { Plus, UserCheck, Package, Clock, LogOut } from 'lucide-react';
import { Card, Button, Input, Select, Table, Modal, Badge } from '../components/ui';
import { visitantesService } from '../services/api';
import { formatDateTime, getLocalDate } from '../utils/formatters';
import { TIPOS_ENTREGA } from '../utils/constants';
import toast from 'react-hot-toast';

const ControlAccesoPage = () => {
  const [tab, setTab] = useState('visitantes');
  const [visitantes, setVisitantes] = useState([]);
  const [entregas, setEntregas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisitante, setModalVisitante] = useState(false);
  const [modalEntrega, setModalEntrega] = useState(false);
  
  const [formVisitante, setFormVisitante] = useState({
    nombre: '', dni: '', departamento_destino: '', motivo: '', vehiculo_placa: ''
  });
  
  const [formEntrega, setFormEntrega] = useState({
    tipo: 'PAQUETE', empresa: '', descripcion: '', departamento_destino: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [visRes, entRes] = await Promise.all([
        visitantesService.getAll({ fecha: getLocalDate() }),
        visitantesService.getEntregas()
      ]);
      setVisitantes(visRes.data.data || []);
      setEntregas(entRes.data.data || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitVisitante = async (e) => {
    e.preventDefault();
    try {
      await visitantesService.create(formVisitante);
      toast.success('Visitante registrado');
      setModalVisitante(false);
      setFormVisitante({ nombre: '', dni: '', departamento_destino: '', motivo: '', vehiculo_placa: '' });
      loadData();
    } catch (error) {
      toast.error('Error al registrar');
    }
  };

  const handleSubmitEntrega = async (e) => {
    e.preventDefault();
    try {
      await visitantesService.createEntrega(formEntrega);
      toast.success('Entrega registrada');
      setModalEntrega(false);
      setFormEntrega({ tipo: 'PAQUETE', empresa: '', descripcion: '', departamento_destino: '' });
      loadData();
    } catch (error) {
      toast.error('Error al registrar');
    }
  };

  const handleSalida = async (id) => {
    try {
      await visitantesService.registrarSalida(id);
      toast.success('Salida registrada');
      loadData();
    } catch (error) {
      toast.error('Error al registrar salida');
    }
  };

  const handleEntregar = async (id) => {
    try {
      await visitantesService.entregarPaquete(id, {});
      toast.success('Entrega completada');
      loadData();
    } catch (error) {
      toast.error('Error al entregar');
    }
  };

  const visitantesColumns = [
    { header: 'Nombre', accessor: 'nombre' },
    { header: 'DNI', accessor: 'dni' },
    { header: 'Destino', render: (row) => `Dpto ${row.departamento_destino}` },
    { header: 'Motivo', accessor: 'motivo' },
    { header: 'Entrada', render: (row) => formatDateTime(row.hora_entrada) },
    { header: 'Salida', render: (row) => row.hora_salida ? formatDateTime(row.hora_salida) : (
      <Button size="xs" variant="secondary" icon={LogOut} onClick={() => handleSalida(row.id)}>Salida</Button>
    )},
    { header: 'Estado', render: (row) => (
      <Badge variant={row.hora_salida ? 'default' : 'success'}>
        {row.hora_salida ? 'Salió' : 'En edificio'}
      </Badge>
    )}
  ];

  const entregasColumns = [
    { header: 'Tipo', render: (row) => (
      <Badge variant="info">{TIPOS_ENTREGA.find(t => t.value === row.tipo)?.label || row.tipo}</Badge>
    )},
    { header: 'Empresa', accessor: 'empresa' },
    { header: 'Descripción', accessor: 'descripcion' },
    { header: 'Destino', render: (row) => `Dpto ${row.departamento_destino}` },
    { header: 'Recepción', render: (row) => formatDateTime(row.hora_recepcion) },
    { header: 'Estado', render: (row) => (
      <Badge variant={row.estado === 'ENTREGADO' ? 'success' : 'warning'}>{row.estado}</Badge>
    )},
    { header: 'Acciones', render: (row) => row.estado === 'PENDIENTE' && (
      <Button size="xs" onClick={() => handleEntregar(row.id)}>Entregar</Button>
    )}
  ];

  const visitantesEnEdificio = visitantes.filter(v => !v.hora_salida).length;
  const entregasPendientes = entregas.filter(e => e.estado === 'PENDIENTE').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Control de Acceso</h1>
          <p className="text-slate-400">Registro de visitantes y entregas</p>
        </div>
        <div className="flex gap-2">
          <Button icon={Plus} onClick={() => setModalVisitante(true)}>Visitante</Button>
          <Button icon={Package} variant="secondary" onClick={() => setModalEntrega(true)}>Entrega</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <UserCheck className="text-emerald-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-slate-400">En el edificio</p>
              <p className="text-xl font-bold text-white">{visitantesEnEdificio}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Clock className="text-blue-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Visitas hoy</p>
              <p className="text-xl font-bold text-white">{visitantes.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Package className="text-amber-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Entregas pendientes</p>
              <p className="text-xl font-bold text-white">{entregasPendientes}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <button 
          onClick={() => setTab('visitantes')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${tab === 'visitantes' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          Visitantes
        </button>
        <button 
          onClick={() => setTab('entregas')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${tab === 'entregas' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
        >
          Entregas
        </button>
      </div>

      {/* Tables */}
      <Card className="overflow-hidden">
        {tab === 'visitantes' ? (
          <Table columns={visitantesColumns} data={visitantes} loading={loading} emptyMessage="No hay visitantes hoy" />
        ) : (
          <Table columns={entregasColumns} data={entregas} loading={loading} emptyMessage="No hay entregas" />
        )}
      </Card>

      {/* Modal Visitante */}
      <Modal isOpen={modalVisitante} onClose={() => setModalVisitante(false)} title="Registrar Visitante">
        <form onSubmit={handleSubmitVisitante} className="space-y-4">
          <Input label="Nombre completo" value={formVisitante.nombre} onChange={(e) => setFormVisitante({ ...formVisitante, nombre: e.target.value })} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="DNI" value={formVisitante.dni} onChange={(e) => setFormVisitante({ ...formVisitante, dni: e.target.value })} />
            <Input label="Dpto. Destino" value={formVisitante.departamento_destino} onChange={(e) => setFormVisitante({ ...formVisitante, departamento_destino: e.target.value })} required />
          </div>
          <Input label="Motivo" value={formVisitante.motivo} onChange={(e) => setFormVisitante({ ...formVisitante, motivo: e.target.value })} />
          <Input label="Placa vehículo" value={formVisitante.vehiculo_placa} onChange={(e) => setFormVisitante({ ...formVisitante, vehiculo_placa: e.target.value })} placeholder="Opcional" />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalVisitante(false)}>Cancelar</Button>
            <Button type="submit">Registrar</Button>
          </div>
        </form>
      </Modal>

      {/* Modal Entrega */}
      <Modal isOpen={modalEntrega} onClose={() => setModalEntrega(false)} title="Registrar Entrega">
        <form onSubmit={handleSubmitEntrega} className="space-y-4">
          <Select label="Tipo" value={formEntrega.tipo} onChange={(e) => setFormEntrega({ ...formEntrega, tipo: e.target.value })} options={TIPOS_ENTREGA} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Empresa" value={formEntrega.empresa} onChange={(e) => setFormEntrega({ ...formEntrega, empresa: e.target.value })} />
            <Input label="Dpto. Destino" value={formEntrega.departamento_destino} onChange={(e) => setFormEntrega({ ...formEntrega, departamento_destino: e.target.value })} required />
          </div>
          <Input label="Descripción" value={formEntrega.descripcion} onChange={(e) => setFormEntrega({ ...formEntrega, descripcion: e.target.value })} />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalEntrega(false)}>Cancelar</Button>
            <Button type="submit">Registrar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ControlAccesoPage;
